import React, { useEffect, useState } from 'react';
import { learnerApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/common/Spinner';
import { FaUser, FaEnvelope, FaPhone, FaBookOpen, FaChartBar, FaFileAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const gradeColor = v => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = v => v >= 80 ? 'A' : v >= 70 ? 'B' : v >= 60 ? 'C' : v >= 50 ? 'D' : v >= 40 ? 'E' : 'F';

export default function LearnerSubjects() {
  const { school } = useAuth();
  const navigate   = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);

  const primary = school?.primary_color || '#0d6efd';

  useEffect(() => {
    learnerApi.getSubjects()
      .then(r => {
        const list = r.data?.data || [];
        setSubjects(list);
        if (list.length > 0) setSelected(list[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  if (subjects.length === 0) return (
    <div>
      <div className="page-header"><h2>My Subjects</h2></div>
      <div className="sms-card" style={{ textAlign: 'center', color: '#9ca3af', padding: '60px 0' }}>
        No subjects assigned to your grade yet
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>My Subjects</h2>
        <p>{subjects.length} subject{subjects.length !== 1 ? 's' : ''} in your grade</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Subject list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {subjects.map(s => {
            const avg  = parseFloat(s.avg_pct);
            const col  = s.avg_pct !== null ? gradeColor(avg) : '#9ca3af';
            const isSelected = selected?.subject_id === s.subject_id;
            return (
              <button key={s.subject_id} onClick={() => setSelected(s)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
                  borderRadius: 12, border: `2px solid ${isSelected ? primary : '#e5e7eb'}`,
                  background: isSelected ? `${primary}0d` : '#fff',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                }}>
                {/* Subject colour dot */}
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: col, flexShrink: 0 }} />
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: isSelected ? primary : '#111827',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.subject_name}
                  </div>
                  {s.subject_code && <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{s.subject_code}</div>}
                </div>
                {s.avg_pct !== null && (
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: col, flexShrink: 0 }}>
                    {avg}%
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Subject detail */}
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Header */}
            <div className="sms-card" style={{ borderTop: `4px solid ${primary}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h3 style={{ fontWeight: 800, color: '#111827', margin: 0 }}>{selected.subject_name}</h3>
                  {selected.subject_code && (
                    <span style={{ background: `${primary}22`, color: primary, fontSize: '0.75rem', fontWeight: 700,
                      padding: '3px 10px', borderRadius: 20, marginTop: 6, display: 'inline-block' }}>
                      {selected.subject_code}
                    </span>
                  )}
                </div>
                {/* My performance */}
                {selected.avg_pct !== null && (
                  <div style={{ textAlign: 'center', background: `${gradeColor(parseFloat(selected.avg_pct))}11`,
                    border: `1px solid ${gradeColor(parseFloat(selected.avg_pct))}33`,
                    borderRadius: 12, padding: '10px 20px' }}>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: gradeColor(parseFloat(selected.avg_pct)), lineHeight: 1 }}>
                      {gradeSymbol(parseFloat(selected.avg_pct))}
                    </div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: gradeColor(parseFloat(selected.avg_pct)) }}>
                      {selected.avg_pct}%
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>My average</div>
                  </div>
                )}
              </div>

              {/* Stats row */}
              <div style={{ display: 'flex', gap: 20, marginTop: 16, flexWrap: 'wrap', fontSize: '0.83rem', color: '#6b7280' }}>
                <span>📝 <strong>{selected.assessment_count || 0}</strong> assessments</span>
                {selected.highest_pct !== null && <span>🏆 Best: <strong style={{ color: '#10b981' }}>{Math.round(selected.highest_pct)}%</strong></span>}
                {selected.lowest_pct !== null  && <span>📉 Lowest: <strong style={{ color: '#ef4444' }}>{Math.round(selected.lowest_pct)}%</strong></span>}
                <span>📚 <strong>{selected.material_count || 0}</strong> materials available</span>
              </div>
            </div>

            {/* Teacher card */}
            {selected.teacher_id ? (
              <div className="sms-card">
                <h5 style={{ fontWeight: 700, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FaUser color={primary} /> Subject Teacher
                </h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${primary}22`,
                    border: `3px solid ${primary}44`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {selected.teacher_avatar
                      ? <img src={selected.teacher_avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontWeight: 800, fontSize: '1.2rem', color: primary }}>
                          {selected.teacher_first_name?.[0]}{selected.teacher_surname?.[0]}
                        </span>
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
                      {selected.teacher_first_name} {selected.teacher_surname}
                    </div>
                    {selected.teacher_qualification && (
                      <div style={{ color: '#6b7280', fontSize: '0.82rem', marginTop: 2 }}>{selected.teacher_qualification}</div>
                    )}
                    <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                      {selected.teacher_email && (
                        <a href={`mailto:${selected.teacher_email}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, color: primary, fontSize: '0.83rem', textDecoration: 'none' }}>
                          <FaEnvelope size={12} /> {selected.teacher_email}
                        </a>
                      )}
                      {selected.teacher_phone && (
                        <a href={`tel:${selected.teacher_phone}`}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, color: primary, fontSize: '0.83rem', textDecoration: 'none' }}>
                          <FaPhone size={12} /> {selected.teacher_phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="sms-card" style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                No teacher assigned to this subject yet
              </div>
            )}

            {/* Quick actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              {[
                { label: 'View Marks',      icon: FaChartBar,  color: primary,    to: '/learner/marks',     param: selected.subject_id },
                { label: 'Study Materials', icon: FaBookOpen,  color: '#10b981',  to: '/learner/materials', param: selected.subject_id },
                { label: 'My Analytics',    icon: FaFileAlt,   color: '#8b5cf6',  to: '/learner/analytics', param: null },
              ].map(a => (
                <button key={a.label}
                  onClick={() => navigate(a.to, { state: { subjectId: a.param } })}
                  style={{ padding: '14px 10px', borderRadius: 12, border: `1px solid ${a.color}33`,
                    background: `${a.color}09`, cursor: 'pointer', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = `${a.color}18`}
                  onMouseLeave={e => e.currentTarget.style.background = `${a.color}09`}>
                  <a.icon size={20} color={a.color} />
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: a.color, textAlign: 'center' }}>{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
